import { Component, OnInit, OnDestroy, ChangeDetectorRef, inject } from '@angular/core';
import { RegisterClass } from '@memberjunction/global';
import { BaseResourceComponent, NavigationService } from '@memberjunction/ng-shared';
import { RunView, RunQuery, CompositeKey, Metadata } from '@memberjunction/core';
import { BCBaseComponent } from '@bluecypress/bcsaas-ng-core';

// ── Types ──

interface ControllerRunItem {
  runID: string;
  organizationName: string;
  personName: string;
  conversationName: string | null;
  status: string;
  durationMs: number | null;
  totalTokens: number;
  totalCost: number;
  startedAt: string;
  completedAt: string | null;
  externalReferenceID: string | null;
  errorMessage: string | null;
}

type RunTimePreset = '1h' | '24h' | '7d' | 'all-time' | 'custom';

interface QueryResult {
  Success: boolean;
  Results?: Record<string, unknown>[];
}

@Component({
  standalone: false,
  selector: 'skip-monitoring-agent-runs',
  templateUrl: './agent-runs.component.html',
  styleUrls: ['./agent-runs.component.css']
})
@RegisterClass(BaseResourceComponent, 'SkipMonitoringAgentRunsResource')
export class SkipMonitoringAgentRunsComponent extends BCBaseComponent implements OnInit, OnDestroy {
  // Search
  RunIDSearch = '';
  ExtRefIDSearch = '';

  // Filters
  TimePreset: RunTimePreset = '24h';
  CustomStartDate = '';
  CustomEndDate = '';
  SelectedStatuses: string[] = [];
  Runs: ControllerRunItem[] = [];
  RunsLoading = false;
  Error: string | null = null;

  // Auto-refresh
  AutoRefreshInterval: number | null = null;
  AutoRefreshTimer: ReturnType<typeof setInterval> | null = null;
  readonly RefreshOptions = [
    { label: 'Off', value: 0 },
    { label: '10s', value: 10000 },
    { label: '30s', value: 30000 },
    { label: '60s', value: 60000 }
  ];

  // Authorization
  IsAuthorizedUser = false;
  IsInitializing = true;

  readonly StatusOptions = ['Running', 'Completed', 'Failed', 'Cancelled'];
  readonly TimePresets: RunTimePreset[] = ['1h', '24h', '7d', 'all-time', 'custom'];

  private cdr = inject(ChangeDetectorRef);
  protected override navigationService = inject(NavigationService);

  constructor() {
    super();
  }

  async GetResourceDisplayName(): Promise<string> {
    return 'Agent Runs';
  }

  async GetResourceIconClass(): Promise<string> {
    return 'fa-solid fa-play';
  }

  override async ngOnInit(): Promise<void> {
    super.ngOnInit();

    this.IsAuthorizedUser = this.checkAuthorization();
    this.IsInitializing = false;
    this.cdr.detectChanges();

    if (!this.IsAuthorizedUser) {
      this.NotifyLoadComplete();
      return;
    }

    await this.LoadRuns();
    this.NotifyLoadComplete();
  }

  override ngOnDestroy(): void {
    this.clearAutoRefresh();
  }

  // ========== Authorization ==========

  private checkAuthorization(): boolean {
    const ctx = this.tenantContextService.currentContext;
    const memberships = this.tenantContextService.GetAllMemberships();
    const isSystemUser = !ctx?.personID && memberships.length === 0;
    const currentUser = Metadata.Provider.CurrentUser;
    const hasIntegrationRole = currentUser?.UserRoles?.some(
      r => r.Role?.toLowerCase() === 'integration'
    ) ?? false;
    return isSystemUser || hasIntegrationRole;
  }

  // ========== Data Loading ==========

  async LoadRuns(): Promise<void> {
    this.RunsLoading = true;
    this.Error = null;
    this.cdr.detectChanges();

    try {
      const rq = new RunQuery();
      const { start, end } = this.getTimeRange();
      const params: Record<string, string> = { StartDate: start, EndDate: end };
      if (this.SelectedStatuses.length === 1) {
        params['Status'] = this.SelectedStatuses[0];
      }

      const result = await rq.RunQuery({
        QueryName: 'MonitoringRecentAgentRuns',
        CategoryPath: '/Skip/Monitoring',
        Parameters: params
      });

      this.Runs = this.parseRunResults(result);
      this.applyClientStatusFilter();
    } catch (e: unknown) {
      this.Error = e instanceof Error ? e.message : 'Failed to load agent runs';
    } finally {
      this.RunsLoading = false;
      this.cdr.detectChanges();
    }
  }

  private parseRunResults(result: QueryResult): ControllerRunItem[] {
    if (!result.Success || !result.Results?.length) return [];

    return result.Results.map(r => ({
      runID: (r['RunID'] as string) || '',
      organizationName: (r['OrganizationName'] as string) || '',
      personName: (r['PersonName'] as string) || '',
      conversationName: (r['ConversationName'] as string) || null,
      status: (r['Status'] as string) || 'Unknown',
      durationMs: (r['DurationMs'] as number) || null,
      totalTokens: (r['TotalTokens'] as number) || 0,
      totalCost: (r['TotalCost'] as number) || 0,
      startedAt: (r['StartedAt'] as string) || '',
      completedAt: (r['CompletedAt'] as string) || null,
      externalReferenceID: (r['ExternalReferenceID'] as string) || null,
      errorMessage: (r['ErrorMessage'] as string) || null
    }));
  }

  private applyClientStatusFilter(): void {
    if (this.SelectedStatuses.length > 1) {
      this.Runs = this.Runs.filter(r => this.SelectedStatuses.includes(r.status));
    }
  }

  // ========== Search ==========

  async SearchByRunID(): Promise<void> {
    const searchID = this.RunIDSearch.trim();
    if (!searchID) return;
    this.RunIDSearch = '';

    try {
      const resolved = await this.resolveRunID(searchID);
      this.openAgentRunForm(resolved);
    } catch {
      this.openAgentRunForm(searchID);
    }
  }

  private async resolveRunID(searchID: string): Promise<string> {
    const rv = new RunView();
    const result = await rv.RunView({
      EntityName: 'Conversation Details__Skip',
      ExtraFilter: `AIAgentRunID = '${searchID}' OR ID = '${searchID}' OR ExternalID = '${searchID}'`,
      Fields: ['ID', 'AIAgentRunID'],
      MaxRows: 1
    });

    if (result.Success && result.Results?.length) {
      const runID = (result.Results[0] as Record<string, unknown>)['AIAgentRunID'] as string;
      if (runID) return runID;
    }
    return searchID;
  }

  async SearchByExternalRefID(): Promise<void> {
    const searchID = this.ExtRefIDSearch.trim();
    if (!searchID) return;
    this.ExtRefIDSearch = '';

    try {
      const runID = await this.resolveExternalRefID(searchID);
      this.openAgentRunForm(runID);
    } catch {
      this.openAgentRunForm(searchID);
    }
  }

  private async resolveExternalRefID(searchID: string): Promise<string> {
    const rv = new RunView();
    const result = await rv.RunView({
      EntityName: 'MJ: AI Agent Runs',
      ExtraFilter: `ExternalReferenceID = '${searchID}'`,
      Fields: ['ID'],
      MaxRows: 1
    });

    if (result.Success && result.Results?.length) {
      return (result.Results[0] as Record<string, unknown>)['ID'] as string;
    }
    return searchID;
  }

  // ========== Navigation ==========

  OpenRunForm(run: ControllerRunItem): void {
    this.openAgentRunForm(run.runID);
  }

  private openAgentRunForm(runID: string): void {
    if (!runID) return;
    this.navigationService.OpenEntityRecord('MJ: AI Agent Runs', CompositeKey.FromID(runID));
  }

  // ========== Filter Actions ==========

  private getTimeRange(): { start: string; end: string } {
    const now = new Date();
    if (this.TimePreset === 'custom') {
      if (this.CustomStartDate && this.CustomEndDate) {
        return {
          start: new Date(this.CustomStartDate).toISOString(),
          end: new Date(this.CustomEndDate).toISOString()
        };
      }
      return { start: new Date(now.getTime() - 86400000).toISOString(), end: now.toISOString() };
    }
    if (this.TimePreset === 'all-time') {
      return { start: new Date(2020, 0, 1).toISOString(), end: now.toISOString() };
    }
    const ms: Record<string, number> = {
      '1h': 3600000,
      '24h': 86400000,
      '7d': 604800000
    };
    return {
      start: new Date(now.getTime() - ms[this.TimePreset]).toISOString(),
      end: now.toISOString()
    };
  }

  SetTimePreset(preset: RunTimePreset): void {
    this.TimePreset = preset;
    this.LoadRuns();
  }

  ToggleStatus(status: string): void {
    const idx = this.SelectedStatuses.indexOf(status);
    if (idx >= 0) {
      this.SelectedStatuses.splice(idx, 1);
    } else {
      this.SelectedStatuses.push(status);
    }
    this.LoadRuns();
  }

  IsStatusSelected(status: string): boolean {
    return this.SelectedStatuses.includes(status);
  }

  // ========== Auto-Refresh ==========

  SetAutoRefresh(ms: number): void {
    this.clearAutoRefresh();
    this.AutoRefreshInterval = ms || null;
    if (ms > 0) {
      this.AutoRefreshTimer = setInterval(() => this.LoadRuns(), ms);
    }
  }

  private clearAutoRefresh(): void {
    if (this.AutoRefreshTimer) {
      clearInterval(this.AutoRefreshTimer);
      this.AutoRefreshTimer = null;
    }
  }

  // ========== Display Helpers ==========

  GetStatusClass(status: string): string {
    const map: Record<string, string> = {
      Running: 'status-running',
      Completed: 'status-completed',
      Failed: 'status-failed',
      Cancelled: 'status-cancelled'
    };
    return map[status] || '';
  }

  FormatDuration(ms: number | null): string {
    if (ms === null || ms === undefined) return '\u2014';
    if (ms < 1000) return `${ms}ms`;
    const totalSeconds = Math.floor(ms / 1000);
    if (totalSeconds < 60) return `${totalSeconds}s`;
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}m ${seconds}s`;
  }

  FormatTokens(count: number | null): string {
    if (!count) return '\u2014';
    if (count >= 1e6) return `${(count / 1e6).toFixed(1)}M`;
    if (count >= 1e3) return `${(count / 1e3).toFixed(1)}K`;
    return count.toString();
  }

  FormatCost(cost: number | null): string {
    if (!cost) return '\u2014';
    return `$${cost.toFixed(4)}`;
  }

  FormatDate(iso: string | null): string {
    if (!iso) return '\u2014';
    return new Date(iso).toLocaleString('en-US', {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
    });
  }

  TruncateID(id: string): string {
    if (!id) return '\u2014';
    return id.substring(0, 8);
  }

  CopyToClipboard(text: string, event: Event): void {
    event.stopPropagation();
    navigator.clipboard.writeText(text);
  }

  ApplyCustomRange(): void {
    this.TimePreset = 'custom';
    this.LoadRuns();
  }
}
